import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, useAuth } from "../App";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token found.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(async (res) => {
        setStatus("success");
        setMessage(res.data.message || "Email verified!");
        await refreshUser();
      })
      .catch(err => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Verification failed.");
      });
  }, [token, refreshUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="verify-email-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 w-full max-w-md text-center"
      >
        {status === "verifying" && (
          <>
            <Loader2 className="text-[#F7931A] mx-auto mb-4 animate-spin" size={48} />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Verifying email...</h2>
            <p className="text-zinc-500">Hang tight, we're confirming your email address.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="text-green-400 mx-auto mb-4" size={48} />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Email Verified!</h2>
            <p className="text-zinc-400 mb-6">{message}</p>
            <Button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="verify-go-dashboard">
              Go to Dashboard
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="text-red-400 mx-auto mb-4" size={48} />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-zinc-400 mb-6">{message}</p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/login")} className="btn-primary w-full" data-testid="verify-go-login">
                Go to Login
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
